/**
 * First-run setup (phase 16g). Mirrors `src/http/setup.rs`.
 *
 * ⚠️ **Both routes answer `404` once an admin exists**, and `POST` answers `404`
 * for a wrong token too — a prober cannot tell "wrong token" from "already set
 * up". So a screen must read `not_found` as *"this installation is already
 * claimed, or you have the wrong token"*, never as a broken link.
 *
 * There is no way to ask which of those it was, deliberately. The deployer has
 * the boot log; anybody else has no business distinguishing them.
 */

import { apiGet, apiPost } from "./core.js";

// ⚠️ **The paths below carry no `/api` prefix, and that is not a style choice.**
// `core.ts`'s `api()` prepends it. These two calls passed `"/api/setup"` from
// 16g-1 until 16i, so the browser asked for `/api/api/setup` and got a 404 —
// and because `SetupPage` renders its "nothing to configure" screen on *any*
// failure, the setup page looked like a claimed installation to everybody who
// ever opened it. It had never worked in a browser.
//
// Nothing caught it: `tests/http_setup.rs` drives the axum Router directly with
// the server's own path, and `SetupPage.test.tsx` mocks this module wholesale.
// Neither ever built the URL. `setup_url_matches_the_server_route` does.

/** One genre, for the feed-scope step. */
export interface SetupGenre {
  id: string;
  slug: string;
  name: string;
  parent_id: string | null;
}

/** `GET /api/setup` — only ever returned while setup is open. */
export interface SetupState {
  /** Always `true`; a closed installation answers `404` instead. */
  open: boolean;
  /** Whether a token from the boot log must be presented. Always `true` today. */
  token_required: boolean;
  /**
   * Every genre, for the feed-scope step (phase 16g-2).
   *
   * ⚠️ Carried here rather than behind a route of its own: a new public endpoint
   * would outlive setup, and this one already `404`s the moment an admin exists.
   */
  genres: SetupGenre[];
  decades: number[];
}

/** `POST /api/setup` */
export interface SetupRequest {
  /** The token printed to the server log at first boot. */
  token: string;
  email: string;
  password: string;
  display_name: string;
  /**
   * What this installation will serve — phase 16g-2.
   *
   * **Optional, and empty means everything.** A deployer who has ingested
   * nothing has no basis for narrowing, and `/admin` changes it afterwards.
   */
  genres?: string[];
  decades?: number[];
}

/**
 * Whether this installation still needs its first admin.
 *
 * Throws `HttpError` with `not_found` when setup is closed, which is the normal
 * case for every installation that has ever been set up.
 */
export async function fetchSetupState(): Promise<SetupState> {
  return apiGet<SetupState>("/setup");
}

/** Create the first admin. */
export async function completeSetup(request: SetupRequest): Promise<{ ok: boolean }> {
  return apiPost<{ ok: boolean }>("/setup", request);
}
